import React, { useContext, useEffect, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Slide,
  useMediaQuery,
  useScrollTrigger,
  useTheme,
  alpha,
} from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import HomeIcon from "@mui/icons-material/Home"
import VideocamIcon from "@mui/icons-material/Videocam"
import AdsClickIcon from "@mui/icons-material/AdsClick"
import TouchAppIcon from "@mui/icons-material/TouchApp"
import SearchIcon from "@mui/icons-material/Search"
import BarChartIcon from "@mui/icons-material/BarChart"
import PlaceIcon from "@mui/icons-material/Place"

const HeaderSubtitleContext = React.createContext(() => {})

/**
 * Show `subtitle` in the app bar next to the SpyClimb logo while the
 * calling page is mounted (e.g. the current gym's name).
 */
export function useHeaderSubtitle(subtitle) {
  const setSubtitle = useContext(HeaderSubtitleContext)
  useEffect(() => {
    setSubtitle(subtitle ?? null)
    return () => setSubtitle(null)
  }, [subtitle, setSubtitle])
}

const navItems = [
  { label: "Home", path: "/", icon: <HomeIcon /> },
  { label: "Stream", path: "/stream", icon: <VideocamIcon /> },
  { label: "Project", path: "/project", icon: <AdsClickIcon /> },
  { label: "Set", path: "/set", icon: <TouchAppIcon /> },
  { label: "Search", path: "/search-climbs", icon: <SearchIcon /> },
  { label: "Gyms", path: "/gyms", icon: <PlaceIcon /> },
  { label: "Stats", path: "/stats", icon: <BarChartIcon /> },
]

export default function Layout({ children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [subtitle, setSubtitle] = useState(null)
  // Hide the app bar while scrolling down, bring it back on scroll up
  const hideAppBar = useScrollTrigger()

  const isActive = (path) => location.pathname === path

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Slide appear={false} direction="down" in={!hideAppBar}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: alpha(theme.palette.background.default, 0.8),
            backdropFilter: "blur(12px)",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, md: 64 } }}>
            {isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => setDrawerOpen(true)}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Typography
              variant="h4"
              onClick={() => navigate("/")}
              sx={{
                cursor: "pointer",
                fontFamily: "'Roboto Condensed', sans-serif",
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mr: subtitle ? 1.5 : 4,
                fontSize: { xs: "1.1rem", md: "1.25rem" },
              }}
            >
              SpyClimb
            </Typography>

            {subtitle && (
              <Typography
                variant="subtitle1"
                noWrap
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.95rem", md: "1.05rem" },
                  mr: { xs: 1, md: 4 },
                  minWidth: 0,
                }}
              >
                {subtitle}
              </Typography>
            )}

            {!isMobile && (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    startIcon={item.icon}
                    sx={{
                      color: isActive(item.path)
                        ? "primary.main"
                        : "text.secondary",
                      bgcolor: isActive(item.path)
                        ? alpha(theme.palette.primary.main, 0.1)
                        : "transparent",
                      borderRadius: 2,
                      px: 2,
                      py: 0.75,
                      fontSize: "0.85rem",
                      fontWeight: isActive(item.path) ? 600 : 400,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: "primary.main",
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}
          </Toolbar>
        </Container>
        </AppBar>
      </Slide>

      {/* Mobile drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            width: 260,
            borderRight: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Box sx={{ pt: 2, pb: 1, px: 2 }}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'Roboto Condensed', sans-serif",
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            SpyClimb
          </Typography>
        </Box>
        <List sx={{ px: 1 }}>
          {navItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(item.path)
                  setDrawerOpen(false)
                }}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  color: isActive(item.path)
                    ? "primary.main"
                    : "text.secondary",
                  bgcolor: isActive(item.path)
                    ? alpha(theme.palette.primary.main, 0.1)
                    : "transparent",
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "inherit",
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.path) ? 600 : 400,
                    fontSize: "0.9rem",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Page content */}
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 2, md: 3 },
          px: { xs: 2, md: 3 },
        }}
      >
        <HeaderSubtitleContext.Provider value={setSubtitle}>
          {children}
        </HeaderSubtitleContext.Provider>
      </Container>
    </Box>
  )
}
